/**
 * 阅后即焚徽章（14.13c；仅私聊，ChatPage 标题区挂载）。时长选择器 21ag 起
 * **拆成懒块** AfterViewSheet——徽章常驻首屏，选择器点了才出现。
 *
 * - 徽章：开启=橙色火焰+紧凑时长（旧版 _buildBurnAfterReadingBadge 同构）；
 *   关闭=灰火焰无文案。**旧版关闭时不渲染徽章**（入口在好友详情开关，14.21），
 *   新版恒显作为唯一入口，刻意偏差——14.21 落地前否则无法开启。
 * - 选择器：移动=底部 sheet / 桌面=锚定徽章下方浮层（PinBanner sheet 同构；
 *   z 25/26 低于消息菜单）；选项 0/10s/30s/60s/5m/1h/1d，当前值勾选。
 * - 选中＝chat/afterView/set（HTTP 成功 Worker 直改会话 afterViewTime 并推
 *   upsert，徽章/输入框随切片自动刷新——无本地乐观，失败 onError toast，
 *   14.13a 口径）。选同值直接关闭零请求。
 */
import { useCallback, useRef, useState } from 'preact/hooks';
import { messagesConnection } from '~/store/appWorker';
import { afterViewCompactLabel } from './afterViewModel';
import { AfterViewSheetLazy } from './AfterViewSheetLazy';
import { IconFlame } from '~/ui/icons';
import styles from './afterView.module.scss';

export function AfterViewControl({
  seconds,
  mobile,
  onError,
}: {
  /** 当前会话 afterViewTime（conversations 切片，推送自动刷新） */
  seconds: number;
  mobile: boolean;
  /** 设置未受理（WS 断/HTTP 失败）：调用方 toast */
  onError: () => void;
}) {
  /** null=关；非空记打开时的锚定位（徽章右缘下方，桌面用） */
  const [open, setOpen] = useState<{ top: number; right: number } | null>(null);
  const badgeRef = useRef<HTMLButtonElement>(null);
  const active = seconds > 0;
  const close = useCallback(() => setOpen(null), []);

  const toggle = (): void => {
    if (open !== null) {
      setOpen(null);
      return;
    }
    const rect = badgeRef.current?.getBoundingClientRect();
    setOpen(
      rect !== undefined
        ? { top: rect.bottom + 4, right: window.innerWidth - rect.right }
        : { top: 52, right: 8 },
    );
  };

  const pick = (next: number): void => {
    setOpen(null);
    if (next === seconds) return;
    void messagesConnection()
      .setAfterView(next)
      .then((ok) => {
        if (!ok) onError();
      });
  };

  return (
    <>
      <button
        ref={badgeRef}
        type="button"
        className={active ? `${styles.badge} ${styles.badgeOn}` : styles.badge}
        data-testid="burn-badge"
        data-active={active || undefined}
        aria-label="设置阅后即焚"
        title="设置阅后即焚"
        onClick={toggle}
      >
        <IconFlame class={styles.badgeIcon} />
        {active && <span data-testid="burn-badge-label">{afterViewCompactLabel(seconds)}</span>}
      </button>
      {open !== null && (
        <AfterViewSheetLazy
          anchor={open}
          seconds={seconds}
          mobile={mobile}
          onClose={close}
          onPick={pick}
        />
      )}
    </>
  );
}
